<template>
  <div class="bi-page">

    <!-- Header -->
    <div class="bi-page-header">
      <div>
        <h4 class="mb-0">Business Intelligence</h4>
        <p class="text-muted mb-0" style="font-size:13px;">Operational analysis powered by Dukakit AI</p>
      </div>
      <button class="btn btn-sm btn-outline-secondary" :class="{ 'bi-spinning': refreshing }" @click="refreshAll">
        <i class="bi bi-arrow-clockwise"></i>
      </button>
    </div>

    <!-- ── Phase 1: AI Summary ───────────────────────────────────────────── -->
    <div class="bi-ai-summary-card">
      <div class="bi-ai-summary-header">
        <span class="text-primary"><i class="bi bi-stars mr-1"></i> AI Summary</span>
        <button v-if="!summaryLoading" class="bi-ai-refresh" @click="loadSummary(true)" title="Regenerate">
          <i class="bi bi-arrow-clockwise"></i>
        </button>
      </div>
      <div v-if="summaryLoading" class="bi-ai-shimmer">
        <div class="bi-sh bi-sh--line"></div>
        <div class="bi-sh bi-sh--line mt-2" style="width:85%"></div>
        <div class="bi-sh bi-sh--line mt-2" style="width:70%"></div>
      </div>
      <p v-else-if="summaryText" class="bi-ai-summary-text">{{ summaryText }}</p>
      <p v-else class="text-muted mb-0" style="font-size:13px;">Could not generate summary. <a href="#" @click.prevent="loadSummary(true)">Try again</a></p>
    </div>

    <!-- ── Phase 2: Ask Your Business ───────────────────────────────────── -->
    <div class="bi-ask-card">
      <p class="bi-section-label">Ask Your Business</p>
      <div class="bi-ask-input-wrap">
        <input
          v-model="question"
          type="text"
          class="form-control form-control-sm bi-ask-input"
          placeholder="e.g. Why did my sales drop? Am I profitable? What should I focus on?"
          @keyup.enter="askQuestion"
        />
        <button class="btn btn-sm btn-dark bi-ask-btn" @click="askQuestion" :disabled="!question.trim()">
          <i class="bi bi-send"></i>
        </button>
      </div>
      <small class="text-muted d-block mt-1" style="font-size:12px;">Opens in SharaGPT</small>
    </div>

    <!-- Main data loading -->
    <div v-if="loading" class="bi-section">
      <div class="bi-sh bi-sh--block mb-3"></div>
      <div class="bi-sh bi-sh--block"></div>
    </div>

    <template v-else-if="data">

      <!-- ── Phase 4: Predictive Alerts ───────────────────────────────── -->
      <div v-if="alerts.length" class="bi-section">
        <p class="bi-section-label">
          <i class="bi bi-exclamation-triangle-fill text-warning mr-1"></i>
          Stock Running Out
        </p>
        <div
          v-for="alert in alerts"
          :key="alert.product_id"
          class="bi-alert-row"
          :class="`bi-alert--${alert.urgency}`"
        >
          <div class="bi-alert-info">
            <span class="bi-alert-name">{{ alert.product_name }}</span>
            <span class="bi-alert-meta">{{ alert.daily_rate }} units/day · {{ alert.current_stock }} left</span>
          </div>
          <div class="bi-alert-badge" :class="`bi-urgency--${alert.urgency}`">
            {{ alert.days_remaining }}d
          </div>
        </div>
      </div>

      <!-- ── Maturity Scoring ──────────────────────────────────────────── -->
      <div class="bi-section">
        <p class="bi-section-label">Operational Maturity</p>
        <div class="bi-maturity-card">
          <div class="bi-ring-wrap">
            <svg viewBox="0 0 64 64" class="bi-ring-svg">
              <circle class="bi-ring-bg" cx="32" cy="32" r="26"/>
              <circle
                class="bi-ring-fill"
                cx="32" cy="32" r="26"
                :stroke="maturityColor"
                :stroke-dasharray="ringCircumference"
                :stroke-dashoffset="ringOffset"
              />
            </svg>
            <div class="bi-ring-inner">
              <span class="bi-ring-pct" :style="{color: maturityColor}">{{ data.maturity.overall }}%</span>
            </div>
          </div>
          <div class="bi-maturity-meta">
            <p class="bi-maturity-label" :style="{color: maturityColor}">{{ data.maturity.label }}</p>
            <p v-if="data.summary" class="bi-maturity-sub">{{ data.summary }}</p>
          </div>
        </div>
        <div class="bi-domains">
          <div v-for="d in data.maturity.domains" :key="d.key" class="bi-domain-row">
            <span class="bi-domain-name">{{ d.label }}</span>
            <div class="bi-domain-track">
              <div class="bi-domain-fill" :style="{width: d.score + '%', background: domainColor(d.score)}"></div>
            </div>
            <span class="bi-domain-score" :style="{color: domainColor(d.score)}">{{ d.score }}</span>
          </div>
        </div>
      </div>

      <!-- ── Operational Journey ───────────────────────────────────────── -->
      <div v-if="data.journey" class="bi-section">
        <p class="bi-section-label">Operational Journey</p>
        <div class="bi-journey-card">
          <div class="bi-journey-head">
            <span class="bi-journey-type">{{ data.journey.label }}</span>
            <span class="bi-journey-pos">Stage {{ data.journey.position }} of {{ data.journey.total }}</span>
          </div>
          <div class="bi-journey-track">
            <div
              v-for="(stage, i) in data.journey.stages"
              :key="i"
              :class="['bi-journey-dot', stage.reached ? 'reached' : '']"
              :title="stage.name"
            ></div>
          </div>
          <p class="bi-journey-next">
            <span v-if="data.journey.position < data.journey.total">
              Next: {{ data.journey.stages[data.journey.position] && data.journey.stages[data.journey.position].name }}
            </span>
            <span v-else class="text-success">Full journey complete</span>
          </p>
        </div>
      </div>

      <!-- ── Operational Insights ──────────────────────────────────────── -->
      <div v-if="visibleInsights.length" class="bi-section">
        <p class="bi-section-label">Operational Insights</p>
        <div class="bi-insights">
          <div
            v-for="insight in visibleInsights"
            :key="insight.id"
            class="bi-insight"
          >
            <div class="bi-insight-stripe" :style="{background: layerColor(insight.layer)}"></div>
            <div class="bi-insight-body">
              <div class="bi-insight-meta">
                <span class="bi-layer-badge" :style="{color: layerColor(insight.layer)}">{{ insight.layer.toUpperCase() }}</span>
                <span class="bi-sev" :class="`bi-sev--${insight.severity}`">{{ insight.severity }}</span>
              </div>
              <p class="bi-insight-title">{{ insight.title }}</p>
              <p class="bi-insight-headline">{{ insight.headline }}</p>
              <p class="bi-insight-text">{{ insight.body }}</p>

              <!-- Phase 3: AI Explanation -->
              <div v-if="insight._explanation" class="bi-ai-explain">
                <i class="bi bi-stars bi-explain-icon"></i>
                <p class="bi-explain-text">{{ insight._explanation }}</p>
              </div>
              <div v-else-if="insight._explaining" class="bi-ai-explain bi-ai-explain--loading">
                <div class="bi-sh bi-sh--line" style="width:90%"></div>
                <div class="bi-sh bi-sh--line mt-1" style="width:70%"></div>
              </div>

              <div class="bi-insight-footer">
                <a v-if="insight.action" :href="insight.action.route" class="bi-insight-cta">
                  {{ insight.action.label }} <i class="bi bi-arrow-right"></i>
                </a>
                <div class="bi-insight-actions">
                  <button
                    v-if="!insight._explanation && !insight._explaining"
                    class="bi-btn-explain"
                    @click="explainInsight(insight)"
                  ><i class="bi bi-stars mr-1"></i>Why this matters</button>
                  <button class="bi-btn-dismiss" @click="dismiss(insight.id)">Dismiss</button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <button v-if="dismissedIds.length" class="bi-show-dismissed" @click="dismissedIds = []">
          Show dismissed ({{ dismissedIds.length }})
        </button>
      </div>

      <p v-else class="text-center text-muted py-4" style="font-size:13px;">
        <i class="bi bi-check-circle text-success"></i> No operational gaps detected.
      </p>

      <p class="bi-generated-at">Analysis updated {{ generatedLabel }}</p>

    </template>

  </div>
</template>

<script>
const CACHE_KEY     = 'bi_intelligence_v2';
const DISMISSED_KEY = 'bi_dismissed_v2';
const CACHE_TTL     = 30 * 60 * 1000;

export default {
  name: 'BusinessIntelligence',

  data() {
    return {
      loading:       false,
      refreshing:    false,
      data:          null,
      dismissedIds:  [],
      alerts:        [],
      // Phase 1
      summaryLoading: false,
      summaryText:    null,
      // Phase 2
      question:      '',
    };
  },

  computed: {
    ringCircumference() { return 2 * Math.PI * 26; },
    ringOffset() {
      return this.ringCircumference * (1 - (this.data?.maturity?.overall ?? 0) / 100);
    },
    maturityColor() {
      const s = this.data?.maturity?.overall ?? 0;
      return s >= 70 ? '#28a745' : s >= 45 ? '#fd7e14' : '#dc3545';
    },
    visibleInsights() {
      return (this.data?.insights ?? [])
        .filter(i => !this.dismissedIds.includes(i.id))
        .map(i => ({ ...i, _explanation: i._explanation || null, _explaining: i._explaining || false }));
    },
    generatedLabel() {
      if (!this.data?.generated_at) return '';
      return new Date(this.data.generated_at).toLocaleString('en-KE', {
        hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short',
      });
    },
  },

  mounted() {
    this.dismissedIds = JSON.parse(localStorage.getItem(DISMISSED_KEY) || '[]');
    this.loadData(false);
    this.loadSummary(false);
    this.loadAlerts();
  },

  methods: {
    async loadData(fresh) {
      if (!fresh) {
        try {
          const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
          if (cached && Date.now() - cached.ts < CACHE_TTL) {
            this.data = cached.data;
            return;
          }
        } catch (_) {}
      }
      this.loading = true;
      try {
        const { data } = await axios.get('/api/business-intelligence', { params: fresh ? { refresh: 1 } : {} });
        this.data = data;
        localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data }));
      } catch (_) {
        this.data = null;
      } finally {
        this.loading = false;
      }
    },

    async loadSummary(fresh) {
      this.summaryLoading = true;
      try {
        const { data } = await axios.get('/api/ai/business-summary', { params: fresh ? { fresh: 1 } : {} });
        this.summaryText = data.summary || null;
      } catch (_) {
        this.summaryText = null;
      } finally {
        this.summaryLoading = false;
      }
    },

    async loadAlerts() {
      try {
        const { data } = await axios.get('/api/ai/predictive-alerts');
        this.alerts = data || [];
      } catch (_) {}
    },

    async refreshAll() {
      this.refreshing = true;
      localStorage.removeItem(CACHE_KEY);
      await Promise.all([
        this.loadData(true),
        this.loadSummary(true),
        this.loadAlerts(),
      ]);
      this.refreshing = false;
    },

    askQuestion() {
      if (!this.question.trim()) return;
      window.location.href = '/sharagpt?q=' + encodeURIComponent(this.question.trim());
    },

    async explainInsight(insight) {
      this.$set(insight, '_explaining', true);
      try {
        const { data } = await axios.post('/api/ai/explain', {
          headline: insight.headline,
          body:     insight.body,
          type:     'insight',
        });
        this.$set(insight, '_explanation', data.explanation || data.error || null);
      } catch (_) {
        this.$set(insight, '_explanation', 'Could not generate explanation.');
      } finally {
        this.$set(insight, '_explaining', false);
      }
    },

    dismiss(id) {
      this.dismissedIds = [...this.dismissedIds, id];
      localStorage.setItem(DISMISSED_KEY, JSON.stringify(this.dismissedIds));
    },

    layerColor(layer) {
      return { risk: '#dc3545', operational: '#fd7e14', efficiency: '#6f42c1', growth: '#28a745' }[layer] || '#6c757d';
    },

    domainColor(score) {
      return score >= 70 ? '#28a745' : score >= 40 ? '#fd7e14' : '#dc3545';
    },
  },
};
</script>

<style scoped>
.bi-page {
  max-width: 700px;
  margin: 0 auto;
  padding: 20px 16px 100px;
}

.bi-page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 20px;
}

.bi-section-label {
  font-size: 0.6rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: #adb5bd;
  margin: 0 0 0.75rem;
}

.bi-section { margin-bottom: 24px; }

/* ── AI Summary ── */
.bi-ai-summary-card {
  background: linear-gradient(135deg, #fafafa 0%, #fafafa 100%);
  border-radius: 12px;
  padding: 18px;
  margin-bottom: 20px;
  color: #000;
  border: #fd4719 1px solid;
}
.bi-ai-summary-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.bi-ai-badge {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #f59e0b;
}
.bi-ai-refresh {
  background: rgba(255,255,255,.12);
  border: none;
  color: rgba(255,255,255,.6);
  border-radius: 50%;
  width: 26px;
  height: 26px;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.bi-ai-refresh:hover { background: rgba(255,255,255,.22); color: #fff; }
.bi-ai-summary-text {
  font-size: 14px;
  line-height: 1.65;
  color: #000 (255,255,255,.9);
  margin: 0;
}

/* ── Ask section ── */
.bi-ask-card {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
}
.bi-ask-input-wrap {
  display: flex;
  gap: 8px;
}
.bi-ask-input { border-radius: 8px; }
.bi-ask-btn { border-radius: 8px; padding: 0 14px; }

/* ── Predictive Alerts ── */
.bi-alert-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 8px;
}
.bi-alert-info { flex: 1; min-width: 0; }
.bi-alert-name { display: block; font-size: 13px; font-weight: 600; color: #111827; }
.bi-alert-meta { display: block; font-size: 11px; color: #6c757d; }
.bi-alert-badge {
  font-size: 13px;
  font-weight: 800;
  padding: 4px 10px;
  border-radius: 20px;
  flex-shrink: 0;
}
.bi-urgency--critical { background: #fde8e8; color: #dc3545; }
.bi-urgency--high     { background: #fff3e0; color: #fd7e14; }
.bi-urgency--medium   { background: #e8f5e9; color: #28a745; }

/* ── Maturity ── */
.bi-maturity-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: #f8f9fa;
  border-radius: 10px;
  padding: 1rem;
  margin-bottom: 1rem;
}
.bi-ring-wrap { position: relative; width: 64px; height: 64px; flex-shrink: 0; }
.bi-ring-svg  { width: 64px; height: 64px; transform: rotate(-90deg); }
.bi-ring-bg   { fill: #fff; stroke: #e9ecef; stroke-width: 4; }
.bi-ring-fill { fill: none; stroke-width: 4; stroke-linecap: round; transition: stroke-dashoffset .7s ease, stroke .4s ease; }
.bi-ring-inner { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; }
.bi-ring-pct { font-size: .9rem; font-weight: 800; }
.bi-maturity-label { font-size: .8rem; font-weight: 700; margin: 0 0 4px; }
.bi-maturity-sub { font-size: .72rem; color: #6c757d; margin: 0; line-height: 1.4; }
.bi-domains { display: flex; flex-direction: column; gap: 7px; }
.bi-domain-row { display: flex; align-items: center; gap: 8px; }
.bi-domain-name { font-size: .7rem; color: #6c757d; width: 78px; flex-shrink: 0; }
.bi-domain-track { flex: 1; height: 5px; background: #e9ecef; border-radius: 3px; overflow: hidden; }
.bi-domain-fill { height: 100%; border-radius: 3px; transition: width .6s ease; }
.bi-domain-score { font-size: .7rem; font-weight: 700; width: 26px; text-align: right; flex-shrink: 0; }

/* ── Journey ── */
.bi-journey-card { background: #f8f9fa; border-radius: 10px; padding: .9rem 1rem; }
.bi-journey-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
.bi-journey-type { font-size: .78rem; font-weight: 600; color: #212529; }
.bi-journey-pos  { font-size: .68rem; color: #6c757d; }
.bi-journey-track { display: flex; align-items: center; gap: 6px; margin-bottom: 8px; }
.bi-journey-dot { width: 10px; height: 10px; border-radius: 50%; background: #dee2e6; flex-shrink: 0; }
.bi-journey-dot.reached { background: #212529; }
.bi-journey-next { font-size: .7rem; color: #6c757d; margin: 0; }

/* ── Insights ── */
.bi-insights { display: flex; flex-direction: column; gap: 10px; }
.bi-insight { display: flex; border-radius: 10px; overflow: hidden; border: 1px solid #e9ecef; background: #fff; }
.bi-insight-stripe { width: 4px; flex-shrink: 0; }
.bi-insight-body { padding: 12px 14px; flex: 1; min-width: 0; }
.bi-insight-meta { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; }
.bi-layer-badge { font-size: .55rem; font-weight: 800; letter-spacing: .1em; }
.bi-sev { font-size: .6rem; text-transform: capitalize; padding: 1px 7px; border-radius: 20px; font-weight: 600; }
.bi-sev--critical    { background: #fde8e8; color: #dc3545; }
.bi-sev--important   { background: #fff3e0; color: #fd7e14; }
.bi-sev--opportunity { background: #e8f5e9; color: #28a745; }
.bi-insight-title    { font-size: .9rem; font-weight: 700; color: #212529; margin: 0 0 3px; }
.bi-insight-headline { font-size: .72rem; font-weight: 600; color: #495057; margin: 0 0 6px; }
.bi-insight-text     { font-size: .72rem; color: #6c757d; margin: 0 0 10px; line-height: 1.5; }
.bi-insight-footer   { display: flex; align-items: center; justify-content: space-between; gap: 8px; flex-wrap: wrap; }
.bi-insight-cta { font-size: .72rem; font-weight: 700; color: #212529; text-decoration: none; }
.bi-insight-cta:hover { opacity: .65; color: #212529; text-decoration: none; }
.bi-insight-actions { display: flex; gap: 10px; align-items: center; }
.bi-btn-explain {
  border: none;
  background: none;
  font-size: .68rem;
  color: #f59e0b;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
}
.bi-btn-explain:hover { color: #d97706; }
.bi-btn-dismiss {
  border: none;
  background: none;
  font-size: .65rem;
  color: #adb5bd;
  cursor: pointer;
  padding: 0;
}
.bi-btn-dismiss:hover { color: #6c757d; }

/* ── AI Explanation block ── */
.bi-ai-explain {
  display: flex;
  gap: 8px;
  background: #fffbeb;
  border-radius: 6px;
  padding: 10px;
  margin-bottom: 10px;
  border: 1px solid #fde68a;
}
.bi-ai-explain--loading { align-items: center; }
.bi-explain-icon { color: #f59e0b; font-size: 14px; flex-shrink: 0; margin-top: 1px; }
.bi-explain-text { font-size: .72rem; color: #374151; line-height: 1.55; margin: 0; }
.bi-show-dismissed {
  width: 100%;
  margin-top: 8px;
  border: none;
  background: none;
  font-size: .7rem;
  color: #adb5bd;
  cursor: pointer;
  padding: 4px 0;
  text-align: center;
}
.bi-show-dismissed:hover { color: #6c757d; }
.bi-generated-at { font-size: .62rem; color: #ced4da; text-align: center; padding: 1rem 0 2rem; margin: 0; }

/* ── Shimmer ── */
.bi-sh {
  background: linear-gradient(90deg, #e9ecef 25%, #f8f9fa 50%, #e9ecef 75%);
  background-size: 200% 100%;
  animation: bi-sh 1.3s infinite;
  border-radius: 4px;
}
.bi-sh--line  { height: 12px; width: 100%; }
.bi-sh--block { height: 80px; border-radius: 10px; }
@keyframes bi-sh { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

.bi-spinning i { animation: bi-spin 1s linear infinite; }
@keyframes bi-spin { to { transform: rotate(360deg); } }
</style>
